import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Save, CheckCircle2, AlertCircle } from "lucide-react";
import { useDossierStore } from "../../../store/dossierStore";
import GrilleSaisieOperations, {
  emptyGrilleRow,
  parseMontantCell,
  rowHasError,
  rowIsSubstantiallyEmpty,
  type GrilleSaisieRow,
} from "../../../components/gestion/GrilleSaisieOperations";
import ImportCSV from "./ImportCSV";
import {
  fetchComptesTresorerieActifs,
  createTransactionsFromSaisie,
  logoUrlPourCompteTresorerieNumero,
  type CompteTresorerieRow,
  type SaisieOperationLine,
} from "../../../lib/gestion/comptesTresorerie";

function grilleToLignes(rows: GrilleSaisieRow[]): SaisieOperationLine[] {
  const out: SaisieOperationLine[] = [];
  for (const r of rows) {
    if (rowIsSubstantiallyEmpty(r)) continue;
    if (rowHasError(r)) continue;
    if (!r.date.trim() || !r.libelle.trim()) continue;
    const d = parseMontantCell(r.debit);
    const c = parseMontantCell(r.credit);
    if (d == null && c == null) continue;
    out.push({ date: r.date.trim(), libelle: r.libelle.trim(), debit: d, credit: c });
  }
  return out;
}

export default function SaisieOperations() {
  const navigate = useNavigate();
  const activeDossier = useDossierStore((s) => s.activeDossier);
  const [comptes, setComptes] = useState<CompteTresorerieRow[]>([]);
  const [compteId, setCompteId] = useState<string>("");
  const [rows, setRows] = useState<GrilleSaisieRow[]>([emptyGrilleRow()]);
  const [csvOpen, setCsvOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const loadComptes = useCallback(async () => {
    if (!activeDossier?.id) return;
    setLoading(true);
    try {
      const list = await fetchComptesTresorerieActifs(activeDossier.id);
      setComptes(list);
      setCompteId((id) => {
        if (id && list.some((c) => c.id === id)) return id;
        return list[0]?.id ?? "";
      });
    } catch {
      setComptes([]);
    } finally {
      setLoading(false);
    }
  }, [activeDossier?.id]);

  useEffect(() => {
    loadComptes();
  }, [loadComptes]);

  const compteSel = comptes.find((c) => c.id === compteId) ?? null;
  const logoCompteSource = compteSel ? logoUrlPourCompteTresorerieNumero(compteSel.numero) : null;

  const handleSave = async () => {
    setMessage(null);
    if (!activeDossier?.id) {
      setMessage({ type: "err", text: "Sélectionnez un dossier." });
      return;
    }
    if (!compteSel) {
      setMessage({ type: "err", text: "Choisissez un compte de trésorerie ou créez-en un dans Paramètres." });
      return;
    }
    if (rows.some(rowHasError)) {
      setMessage({ type: "err", text: "Corrigez les lignes avec débit et crédit simultanés." });
      return;
    }
    const lignes = grilleToLignes(rows);
    if (lignes.length === 0) {
      setMessage({ type: "err", text: "Aucune ligne valide (date, libellé, et débit ou crédit)." });
      return;
    }
    setSaving(true);
    try {
      const n = await createTransactionsFromSaisie(activeDossier.id, compteSel, lignes);
      setMessage({ type: "ok", text: `${n} opération(s) enregistrée(s).` });
      setRows([emptyGrilleRow()]);
    } catch (e) {
      setMessage({ type: "err", text: e instanceof Error ? e.message : "Enregistrement impossible." });
    } finally {
      setSaving(false);
    }
  };

  const handleImport = (imported: GrilleSaisieRow[]) => {
    setRows((prev) => {
      const core = prev.filter((r) => !rowIsSubstantiallyEmpty(r));
      return [...core, ...imported, emptyGrilleRow()];
    });
    setMessage({ type: "ok", text: `${imported.length} ligne(s) ajoutées à la grille.` });
  };

  if (!activeDossier) {
    return (
      <div className="p-8 text-center text-slate-600">
        <p>Sélectionnez un dossier pour saisir des opérations.</p>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 px-4 py-6 md:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex flex-col gap-4 border-b border-slate-200 pb-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Saisie des opérations</h1>
            <p className="mt-1 text-sm text-slate-600">
              Saisie rapide des mouvements bancaires — raccourcis : Tab (cellule suivante), Entrée (ligne suivante),
              Échap (quitter la cellule).
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
              Compte source
              <div className="flex min-w-[220px] items-center gap-2">
                {logoCompteSource ? (
                  <img
                    src={logoCompteSource}
                    alt=""
                    className="h-9 w-9 shrink-0 rounded-lg border border-slate-200 bg-slate-50 object-contain p-0.5"
                  />
                ) : null}
                <select
                  value={compteId}
                  onChange={(e) => setCompteId(e.target.value)}
                  disabled={loading || comptes.length === 0}
                  className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                >
                  {comptes.length === 0 ? (
                    <option value="">Aucun compte actif</option>
                  ) : (
                    comptes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.numero} — {c.libelle}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </label>
            <button
              type="button"
              onClick={() => setCsvOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            >
              <Upload size={18} /> Importer CSV
            </button>
            <button
              type="button"
              onClick={() => navigate("/parametres/entreprise?onglet=tresorerie")}
              className="text-sm text-orange-600 hover:text-orange-700"
            >
              Paramétrer les comptes
            </button>
          </div>
        </header>

        {comptes.length === 0 && !loading ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Aucun compte de trésorerie actif.{" "}
            <button
              type="button"
              className="font-semibold underline"
              onClick={() => navigate("/parametres/entreprise?onglet=tresorerie")}
            >
              Ajoutez-en dans les paramètres
            </button>
            .
          </div>
        ) : null}

        {message ? (
          <div
            className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm ${
              message.type === "ok"
                ? "border border-emerald-200 bg-emerald-50 text-emerald-900"
                : "border border-red-200 bg-red-50 text-red-800"
            }`}
          >
            {message.type === "ok" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            {message.text}
          </div>
        ) : null}

        <GrilleSaisieOperations rows={rows} setRows={setRows} />

        <div className="flex justify-end">
          <button
            type="button"
            disabled={saving || !compteSel}
            onClick={handleSave}
            className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-6 py-3 text-sm font-semibold text-white shadow-md hover:bg-orange-500 disabled:opacity-40"
          >
            <Save size={18} />
            {saving ? "Enregistrement…" : "Enregistrer tout"}
          </button>
        </div>
      </div>

      <ImportCSV open={csvOpen} onClose={() => setCsvOpen(false)} onImport={handleImport} />
    </div>
  );
}
